import { AxiosResponse } from "axios";
import { endPoints } from "utils/constants";
import { crudService } from "services/crud.service";
import { NotificationManager } from "react-notifications";
import { defaultMetaObj, initialCommentState } from "store/constants";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { createAsyncThunk, PayloadAction, createSlice } from "@reduxjs/toolkit";

const { leadCommentEndPoint, tokenCommentEndPoint } = endPoints;

export const addNewComment = createAsyncThunk(
  "comments/addNewComment",
  async (commentData: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${leadCommentEndPoint}/`,
        commentData
      );

      if (response.status === 201) {
        NotificationManager.success("Comment Added Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const addNewCommentInToken = createAsyncThunk(
  "comments/addNewCommentInToken",
  async (commentData: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${tokenCommentEndPoint}/`,
        commentData
      );

      if (response.status === 201) {
        NotificationManager.success("Comment Added Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllComments = createAsyncThunk(
  "comments/fetchAllComments",
  async (
    { page, searchParamsObj = {} }: { page: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: leadCommentEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllTokenComments = createAsyncThunk(
  "comments/fetchAllTokenComments",
  async (
    { page, searchParamsObj = {} }: { page: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: tokenCommentEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

const commentSlice = createSlice({
  name: "comments",
  initialState: initialCommentState,
  reducers: {},
  extraReducers: (builder) => {
    // add new comment
    builder
      .addCase(addNewComment.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(addNewComment.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
      })
      .addCase(addNewComment.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // add new token comment
    builder
      .addCase(addNewCommentInToken.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        addNewCommentInToken.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(addNewCommentInToken.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetch all comment
    builder
      .addCase(fetchAllComments.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllComments.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.comments = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllComments.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.comments = [];
          state.meta = defaultMetaObj;
        }
      );

    // fetch all token comment
    builder
      .addCase(fetchAllTokenComments.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllTokenComments.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.tokenComments = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllTokenComments.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.tokenComments = [];
          state.meta = defaultMetaObj;
        }
      );
  },
});

export const commentsReducer = commentSlice.reducer;
